import { useState } from "react";

import { X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface AliasesEditorProps {
  /** The names the curator adds. The keys the record already has are never part of this list. */
  aliases: string[];
  /** The keys the record already answers to (its own and every merge's): shown, never edited, always kept by the server. */
  knownKeys: string[];
  onChange: (aliases: string[]) => void;
}

/**
 * Other names the festival goes by. A name a page uses that the key rules do not already fold into this festival's key
 * ("ACL" for "Austin City Limits") is added here, so the next page that uses it joins this festival.
 */
export function AliasesEditor({
  aliases,
  knownKeys,
  onChange,
}: AliasesEditorProps) {
  const [draft, setDraft] = useState("");
  const name = draft.trim();

  const add = () => {
    if (name === "") return;
    if (!aliases.includes(name)) onChange([...aliases, name]);
    setDraft("");
  };

  return (
    <div className="flex flex-col gap-3">
      {knownKeys.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">
            Names Tina already knows it by:
          </span>
          {knownKeys.map((key) => (
            <Badge
              key={key}
              variant="secondary"
              className="font-mono text-[11px] font-normal"
            >
              {key}
            </Badge>
          ))}
        </div>
      ) : null}
      {aliases.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {aliases.map((alias) => (
            <li key={alias}>
              <Badge variant="outline" className="gap-1 text-xs font-normal">
                {alias}
                <button
                  type="button"
                  aria-label={`Remove name ${alias}`}
                  className="rounded-sm hover:bg-muted"
                  onClick={() => onChange(aliases.filter((a) => a !== alias))}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex max-w-md gap-2">
        <Input
          aria-label="Add another name"
          placeholder="Another name this festival goes by"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            add();
          }}
        />
        <Button
          type="button"
          variant="outline"
          disabled={name === ""}
          onClick={add}
        >
          Add name
        </Button>
      </div>
    </div>
  );
}
